import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Euro, X, CheckCheck, Loader2, Bell, Send } from "lucide-react";

const isInvio = (n) => (n.tipo || "").startsWith("pronto_invio_");
const notificaLink = (n) => (isInvio(n) ? `/clienti/${n.cliente_id}?tab=${n.tipo.replace("pronto_invio_", "")}` : `/clienti/${n.cliente_id}`);
import { useAuth } from "../context/AuthContext";
import { canDoAction } from "../lib/menu";

const POLL_MS = 30000;

function fmtRel(iso) {
  if (!iso) return "—";
  try {
    const past = Math.round((new Date() - new Date(iso)) / 60000);
    if (past < 1) return "adesso";
    if (past < 60) return `${past} min fa`;
    const h = Math.floor(past / 60);
    if (h < 24) return `${h}h fa`;
    return `${Math.floor(h / 24)}g fa`;
  } catch { return iso; }
}

export default function NotificheBell() {
  const { user } = useAuth();
  const allowed = user?.role === "admin" && canDoAction(user, "economico_view");
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);

  const loadCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifiche/count");
      setUnread(data.unread || 0);
    } catch { /* silent */ }
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifiche", { params: { limit: 30 } });
      setItems(data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!allowed) return;
    loadCount();
    const t = setInterval(loadCount, POLL_MS);
    return () => clearInterval(t);
  }, [allowed, loadCount]);

  useEffect(() => { if (open) loadItems(); }, [open, loadItems]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!allowed) return null;

  const markRead = async (id) => {
    try {
      await api.post(`/notifiche/${id}/read`);
      setItems((arr) => arr.map((n) => (n.id === id ? { ...n, read: true } : n)));
      loadCount();
    } catch { /* silent */ }
  };

  const markAll = async () => {
    try {
      await api.post("/notifiche/read-all");
      setItems((arr) => arr.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch { /* silent */ }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-lg hover:bg-black/5 transition"
        data-testid="notifiche-bell"
        aria-label="Notifiche"
        title={unread > 0 ? `${unread} notifiche da leggere` : "Nessuna nuova notifica"}
      >
        <Bell className={`w-5 h-5 ${unread > 0 ? "text-green-700" : ""}`} />
        {unread > 0 && (
          <span
            data-testid="notifiche-bell-count"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold text-white"
            style={{ background: "#15803D", padding: "0 5px" }}
          >
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[380px] max-h-[480px] bg-white rounded-xl shadow-2xl border z-50 flex flex-col max-md:fixed max-md:left-2 max-md:right-2 max-md:top-16 max-md:mt-0 max-md:w-auto max-md:max-h-[calc(100dvh-80px)]"
          style={{ borderColor: "var(--border)" }}
          data-testid="notifiche-dropdown"
        >
          <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
            <div>
              <div className="font-bold text-sm" style={{ fontFamily: "Manrope" }}>Notifiche</div>
              <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{unread} non lette</div>
            </div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button onClick={markAll} className="inline-flex items-center gap-1 px-2 py-1.5 rounded text-xs md:text-[11px] font-semibold hover:bg-black/5" data-testid="notifiche-read-all">
                  <CheckCheck className="w-3.5 h-3.5" /> Segna tutte lette
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-2 md:p-1 rounded hover:bg-black/5" data-testid="notifiche-close">
                <X className="w-5 h-5 md:w-4 md:h-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading && items.length === 0 && (
              <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline-block" /></div>
            )}
            {!loading && items.length === 0 && (
              <div className="p-8 text-center text-sm" style={{ color: "var(--text-muted)" }}>
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                Nessuna notifica.<br />
                Ti avvisiamo quando un operatore salva la scheda economico di un cliente.
              </div>
            )}
            {items.map((n) => (
              <div
                key={n.id}
                className="px-4 py-3 border-b last:border-b-0"
                style={{ borderColor: "var(--border)", background: n.read ? "white" : "rgba(220,252,231,0.35)" }}
                data-testid={`notifica-item-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  <div className="p-1.5 rounded-md flex-shrink-0" style={{ background: n.read ? "#F3F4F6" : "#DCFCE7" }}>
                    {isInvio(n) ? <Send className="w-3.5 h-3.5" style={{ color: n.read ? "#374151" : "#0D47A1" }} /> : <Euro className="w-3.5 h-3.5" style={{ color: n.read ? "#374151" : "#15803D" }} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">{n.titolo}</div>
                    <div className="text-sm md:text-xs mt-0.5 break-words" style={{ color: "var(--text-muted)" }}>{n.messaggio}</div>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>{fmtRel(n.created_at)}</span>
                      <div className="flex items-center gap-2">
                        {n.cliente_id && (
                          <Link
                            to={notificaLink(n)}
                            onClick={() => { markRead(n.id); setOpen(false); }}
                            className="text-[11px] font-semibold hover:underline"
                            style={{ color: "var(--primary)" }}
                            data-testid={`notifica-link-${n.id}`}
                          >
                            Apri cliente →
                          </Link>
                        )}
                        {!n.read && (
                          <button onClick={() => markRead(n.id)} className="text-[11px] font-semibold hover:underline" data-testid={`notifica-read-${n.id}`}>
                            Segna letta
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
